import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { format } from "date-fns";
import Page from "./Page";
import EntityList from "./EntityList";
import Dialog_Flex from "../ui/Dialog_Flex";
import Dialog_YesNo from "../ui/Dialog_YesNo";
import Input from "../elements/Input";
import Select from "../elements/Select";
import AutoComplete from "../elements/AutoComplete";
import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

const DEFAULT_CUTOFF_TIMEZONE = "America/Toronto";

const CANADIAN_TIMEZONE_ITEMS = [
  { id: "America/St_Johns", name: "Newfoundland (America/St_Johns)" },
  { id: "America/Halifax", name: "Atlantic (America/Halifax)" },
  { id: "America/Toronto", name: "Eastern (America/Toronto)" },
  { id: "America/Winnipeg", name: "Central (America/Winnipeg)" },
  { id: "America/Regina", name: "Saskatchewan (America/Regina)" },
  { id: "America/Edmonton", name: "Mountain (America/Edmonton)" },
  { id: "America/Vancouver", name: "Pacific (America/Vancouver)" },
];

const normalizeCutoffTimezone = (value) => {
  const normalized = String(value || "").trim();
  const found = CANADIAN_TIMEZONE_ITEMS.find((item) => item.id === normalized);
  return found ? found.id : DEFAULT_CUTOFF_TIMEZONE;
};

const OrdersPolicyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { executeAction } = useData();
  const { showMessage } = usePage();

  const clientId = location?.state?.clientId || null;
  const returnPath = location?.state?.returnPath || "/clientorders";
  const returnState = location?.state?.returnState || {};

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [banners, setBanners] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [formData, setFormData] = useState({
    scopeType: "client",
    scopeId: "",
    locationClassCode: "__all__",
    priority: 0,
    isActive: true,
    effectiveFrom: "",
    effectiveTo: "",
    cutoffEnabled: false,
    cutoffLocalTime: "",
    cutoffTimezoneSource: DEFAULT_CUTOFF_TIMEZONE,
    missedCutoffPromptMode: "prompt_next_window_or_cancel",
    minDollar: "",
    maxDollar: "",
    minQuantity: "",
    maxQuantity: "",
    dollarSeverity: "block",
    quantitySeverity: "block",
  });

  useEffect(() => {
    if (!clientId) {
      setRows([]);
      setClassifications([]);
      setRetailers([]);
      setBanners([]);
      return;
    }

    loadPolicyData();
  }, [clientId]);

  const scopeTypeItems = [
    { id: "client", name: "Client" },
    { id: "retailer", name: "Retailer" },
    { id: "banner", name: "Banner" },
  ];

  const severityItems = [
    { id: "block", name: "Block" },
    { id: "warn", name: "Warn" },
  ];

  const cutoffTimezoneItems = CANADIAN_TIMEZONE_ITEMS;

  const missedCutoffModeItems = [
    {
      id: "prompt_next_window_or_cancel",
      name: "Prompt Next Window Or Cancel",
    },
  ];

  const classItems = useMemo(() => {
    const base = [{ id: "__all__", name: "All Classes" }];
    const activeItems = classifications
      .filter((row) => row?.isActive)
      .map((row) => ({
        id: row.code,
        name: `${row.name} (${row.code})`,
      }));

    return [...base, ...activeItems];
  }, [classifications]);

  const sortedRows = useMemo(() => {
    const scopeRank = {
      banner: 0,
      retailer: 1,
      client: 2,
    };

    return [...rows].sort((left, right) => {
      const leftRank = scopeRank[left?.scopeType] ?? 9;
      const rightRank = scopeRank[right?.scopeType] ?? 9;
      if (leftRank !== rightRank) return leftRank - rightRank;

      const leftPriority = Number(left?.priority || 0);
      const rightPriority = Number(right?.priority || 0);
      if (leftPriority !== rightPriority) return rightPriority - leftPriority;

      return String(left?.id || "").localeCompare(String(right?.id || ""));
    });
  }, [rows]);

  const updateFormField = (field, value) => {
    setFormData((previous) => ({ ...previous, [field]: value }));
  };

  const updateScopeType = (value) => {
    setFormData((previous) => ({
      ...previous,
      scopeType: value,
      scopeId: "",
    }));
  };

  const scopeTargetLabel = useMemo(() => {
    if (formData.scopeType === "banner") return "Banner";
    if (formData.scopeType === "retailer") return "Retailer";
    return "Scope Target";
  }, [formData.scopeType]);

  const scopeTargetItems = useMemo(() => {
    if (formData.scopeType === "banner") return banners;
    if (formData.scopeType === "retailer") return retailers;
    return [];
  }, [formData.scopeType, banners, retailers]);

  const toDateInputValue = (value) => {
    if (!value) return "";
    const dateValue = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dateValue.getTime())) return "";
    return format(dateValue, "yyyy-MM-dd");
  };

  const parseNumberOrUndefined = (value) => {
    if (value === "" || value === null || value === undefined) {
      return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const normalizeClassCode = (value) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase();
    return normalized.length > 0 ? normalized : null;
  };

  const loadPolicyData = async () => {
    if (!clientId) return;

    setLoading(true);
    try {
      const [
        policyResults,
        classificationResults,
        retailerResults,
        bannerResults,
      ] = await Promise.all([
        executeAction({
          entityName: "ClientOrderPolicy",
          action: "get",
          filter: { clientId },
        }),
        executeAction({
          entityName: "ClientLocationClassification",
          action: "get",
          filter: { clientId },
        }),
        executeAction({
          entityName: "Retailer",
          action: "get",
          filter: {},
        }),
        executeAction({
          entityName: "Banner",
          action: "get",
          filter: {},
        }),
      ]);

      if (policyResults?.isSuccessful && Array.isArray(policyResults.data)) {
        setRows(policyResults.data);
      } else {
        setRows([]);
      }

      if (
        classificationResults?.isSuccessful &&
        Array.isArray(classificationResults.data)
      ) {
        setClassifications(classificationResults.data);
      } else {
        setClassifications([]);
      }

      if (
        retailerResults?.isSuccessful &&
        Array.isArray(retailerResults.data)
      ) {
        setRetailers(
          retailerResults.data.map((item) => ({
            key: item.id,
            id: item.id,
            name: item.name || item.referenceId || item.id,
          })),
        );
      } else {
        setRetailers([]);
      }

      if (bannerResults?.isSuccessful && Array.isArray(bannerResults.data)) {
        setBanners(
          bannerResults.data.map((item) => ({
            key: item.id,
            id: item.id,
            name: item.name || item.referenceId || item.id,
          })),
        );
      } else {
        setBanners([]);
      }
    } catch (err) {
      setRows([]);
      setClassifications([]);
      setRetailers([]);
      setBanners([]);
      showMessage(err?.message || "Failed to load policies", "error");
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingRecord(null);
    setFormData({
      scopeType: "client",
      scopeId: "",
      locationClassCode: "__all__",
      priority: 0,
      isActive: true,
      effectiveFrom: "",
      effectiveTo: "",
      cutoffEnabled: false,
      cutoffLocalTime: "",
      cutoffTimezoneSource: DEFAULT_CUTOFF_TIMEZONE,
      missedCutoffPromptMode: "prompt_next_window_or_cancel",
      minDollar: "",
      maxDollar: "",
      minQuantity: "",
      maxQuantity: "",
      dollarSeverity: "block",
      quantitySeverity: "block",
    });
    setOpenDialog(true);
  };

  const openEditDialog = (record) => {
    setEditingRecord(record);
    setFormData({
      scopeType: record?.scopeType || "client",
      scopeId: record?.scopeId || "",
      locationClassCode: record?.locationClassCode || "__all__",
      priority: Number(record?.priority || 0),
      isActive: record?.isActive !== false,
      effectiveFrom: toDateInputValue(record?.effectiveFrom),
      effectiveTo: toDateInputValue(record?.effectiveTo),
      cutoffEnabled: record?.behaviorConfig?.cutoffEnabled === true,
      cutoffLocalTime: record?.behaviorConfig?.cutoffLocalTime || "",
      cutoffTimezoneSource: normalizeCutoffTimezone(
        record?.behaviorConfig?.cutoffTimezoneSource,
      ),
      missedCutoffPromptMode:
        record?.behaviorConfig?.missedCutoffPromptMode ||
        "prompt_next_window_or_cancel",
      minDollar:
        record?.validationConfig?.minDollar !== undefined
          ? String(record.validationConfig.minDollar)
          : "",
      maxDollar:
        record?.validationConfig?.maxDollar !== undefined
          ? String(record.validationConfig.maxDollar)
          : "",
      minQuantity:
        record?.validationConfig?.minQuantity !== undefined
          ? String(record.validationConfig.minQuantity)
          : "",
      maxQuantity:
        record?.validationConfig?.maxQuantity !== undefined
          ? String(record.validationConfig.maxQuantity)
          : "",
      dollarSeverity: record?.validationConfig?.dollarSeverity || "block",
      quantitySeverity: record?.validationConfig?.quantitySeverity || "block",
    });
    setOpenDialog(true);
  };

  const savePolicy = async () => {
    if (!clientId) {
      showMessage("Missing client context", "error");
      return;
    }

    if (
      formData.scopeType !== "client" &&
      !String(formData.scopeId || "").trim()
    ) {
      showMessage(
        "Scope target is system-managed and must be assigned before using non-client scope",
        "error",
      );
      return;
    }

    const minDollar = parseNumberOrUndefined(formData.minDollar);
    const maxDollar = parseNumberOrUndefined(formData.maxDollar);
    if (
      minDollar !== undefined &&
      maxDollar !== undefined &&
      minDollar > maxDollar
    ) {
      showMessage("minDollar cannot be greater than maxDollar", "error");
      return;
    }

    const minQuantity = parseNumberOrUndefined(formData.minQuantity);
    const maxQuantity = parseNumberOrUndefined(formData.maxQuantity);
    if (
      minQuantity !== undefined &&
      maxQuantity !== undefined &&
      minQuantity > maxQuantity
    ) {
      showMessage("minQuantity cannot be greater than maxQuantity", "error");
      return;
    }

    if (
      formData.cutoffEnabled &&
      !String(formData.cutoffLocalTime || "").trim()
    ) {
      showMessage("Cutoff time is required when cutoff is enabled", "error");
      return;
    }

    const data = {
      clientId,
      scopeType: formData.scopeType,
      scopeId:
        formData.scopeType === "client"
          ? null
          : String(formData.scopeId || "").trim(),
      locationClassCode:
        formData.locationClassCode === "__all__"
          ? null
          : normalizeClassCode(formData.locationClassCode),
      isActive: !!formData.isActive,
      priority: Number(formData.priority || 0),
      effectiveFrom: formData.effectiveFrom || null,
      effectiveTo: formData.effectiveTo || null,
      behaviorConfig: {
        cutoffEnabled: !!formData.cutoffEnabled,
        cutoffLocalTime: formData.cutoffEnabled
          ? String(formData.cutoffLocalTime || "").trim()
          : null,
        cutoffTimezoneSource: formData.cutoffEnabled
          ? formData.cutoffTimezoneSource
          : null,
        missedCutoffPromptMode: formData.cutoffEnabled
          ? formData.missedCutoffPromptMode
          : null,
      },
      validationConfig: {
        minDollar,
        maxDollar,
        minQuantity,
        maxQuantity,
        dollarSeverity: formData.dollarSeverity,
        quantitySeverity: formData.quantitySeverity,
      },
    };

    setSaving(true);
    try {
      const results = await executeAction({
        entityName: "ClientOrderPolicy",
        action: editingRecord?.id ? "update" : "add",
        data: editingRecord?.id ? { ...data, id: editingRecord.id } : data,
      });

      if (!results?.isSuccessful) {
        showMessage(results?.message || "Failed to save policy", "error");
        return;
      }

      showMessage("Policy saved", "success");
      setOpenDialog(false);
      await loadPolicyData();
    } catch (err) {
      showMessage(err?.message || "Failed to save policy", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToolbarClick = (name) => {
    if (name === "back") {
      navigate(returnPath, { state: returnState });
      return;
    }

    if (name === "add") {
      openAddDialog();
      return;
    }

    if (name === "refresh") {
      loadPolicyData();
    }
  };

  const handleRowClick = (_column, id) => {
    const selected = rows.find((row) => row.id === id);
    if (!selected) return;
    openEditDialog(selected);
  };

  const deletePolicy = async () => {
    if (!editingRecord?.id) return;

    setSaving(true);
    try {
      const results = await executeAction({
        entityName: "ClientOrderPolicy",
        action: "delete",
        data: { id: editingRecord.id },
      });

      if (!results?.isSuccessful) {
        showMessage(results?.message || "Failed to delete policy", "error");
        return;
      }

      showMessage("Policy deleted", "success");
      setOpenDeleteConfirm(false);
      setOpenDialog(false);
      setEditingRecord(null);
      await loadPolicyData();
    } catch (err) {
      showMessage(err?.message || "Failed to delete policy", "error");
    } finally {
      setSaving(false);
    }
  };

  const summarizeRules = (row) => {
    const parts = [];
    const validation = row?.validationConfig || {};
    const behavior = row?.behaviorConfig || {};

    const hasDollarRule =
      validation.minDollar !== undefined || validation.maxDollar !== undefined;
    if (hasDollarRule) {
      const minDollar =
        validation.minDollar !== undefined ? validation.minDollar : "-";
      const maxDollar =
        validation.maxDollar !== undefined ? validation.maxDollar : "-";
      const severity = validation.dollarSeverity || "block";
      parts.push(`$ ${minDollar} to ${maxDollar} (${severity})`);
    }

    const hasQuantityRule =
      validation.minQuantity !== undefined ||
      validation.maxQuantity !== undefined;
    if (hasQuantityRule) {
      const minQty =
        validation.minQuantity !== undefined ? validation.minQuantity : "-";
      const maxQty =
        validation.maxQuantity !== undefined ? validation.maxQuantity : "-";
      const severity = validation.quantitySeverity || "block";
      parts.push(`Qty ${minQty} to ${maxQty} (${severity})`);
    }

    if (behavior.cutoffEnabled && behavior.cutoffLocalTime) {
      const timezoneId = normalizeCutoffTimezone(behavior.cutoffTimezoneSource);
      const timezoneLabel =
        cutoffTimezoneItems.find((item) => item.id === timezoneId)?.name ||
        timezoneId;
      parts.push(`Submit by ${behavior.cutoffLocalTime} (${timezoneLabel})`);
    }

    if (parts.length === 0) {
      return "No rules";
    }

    return parts.join(" | ");
  };

  const tableColumns = [
    { name: "scopeType", label: "Scope", clickable: true, width: "150px" },
    {
      name: "scopeId",
      label: "Scope Target",
      width: "220px",
      render: (row) =>
        row?.scopeType === "client"
          ? "Current client"
          : row?.scopeId || "(unassigned)",
    },
    {
      name: "locationClassCode",
      label: "Class",
      width: "140px",
      render: (row) => row?.locationClassCode || "all",
    },
    {
      name: "priority",
      label: "Priority",
      width: "90px",
    },
    {
      name: "rules",
      label: "Rules",
      width: "440px",
      render: (row) => summarizeRules(row),
    },
    {
      name: "isActive",
      label: "Active",
      width: "90px",
      render: (row) => (row?.isActive ? "Yes" : "No"),
    },
  ];

  return (
    <>
      <Page
        pageName="Order Policies"
        includeToolbar
        onToolItemClick={handleToolbarClick}
        items={[
          { name: "back", label: "Back", iconName: "ArrowBack" },
          { name: "add", label: "Add", iconName: "AddCircleOutline" },
          { name: "refresh", label: "Refresh", iconName: "Refresh" },
        ]}
      >
        {!clientId ? (
          <Typography>Client context is missing for this page.</Typography>
        ) : (
          <Box>
            <Typography sx={{ mb: 1 }}>
              Manage scoped order policies and validation thresholds.
            </Typography>

            {loading ? (
              <Typography>Loading policies...</Typography>
            ) : (
              <EntityList
                entityList={sortedRows}
                columns={tableColumns}
                onRowClick={handleRowClick}
                tableHeight="calc(100vh - 250px)"
                allowSort={false}
              />
            )}
          </Box>
        )}
      </Page>

      <Dialog_Flex
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title={editingRecord?.id ? "Edit Policy" : "Add Policy"}
        message=""
        actions={[
          ...(editingRecord?.id
            ? [{ name: "delete", label: saving ? "deleting" : "delete" }]
            : []),
          { name: "cancel", label: "cancel" },
          { name: "save", label: saving ? "saving" : "save" },
        ]}
        onAction={(action) => {
          if (action === "delete" && editingRecord?.id && !saving) {
            setOpenDeleteConfirm(true);
            return;
          }
          if (action === "cancel") {
            setOpenDialog(false);
            return;
          }
          if (action === "save" && !saving) {
            savePolicy();
          }
        }}
      >
        <Box
          sx={{
            px: 3,
            pb: 2,
            pt: 1,
            minWidth: 520,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <Select
              label="Scope"
              value={formData.scopeType}
              items={scopeTypeItems}
              onChange={(value) => updateScopeType(value)}
            />

            {formData.scopeType === "client" ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Scope Target
                </Typography>
                <Typography>Auto: current client</Typography>
              </Box>
            ) : (
              <AutoComplete
                id="policyScopeTarget"
                label={scopeTargetLabel}
                value={formData.scopeId || ""}
                onChange={(value) => updateFormField("scopeId", value || "")}
                items={scopeTargetItems}
                width="100%"
              />
            )}

            <Select
              label="Classification"
              value={formData.locationClassCode}
              items={classItems}
              onChange={(value) => updateFormField("locationClassCode", value)}
            />

            <Input
              label="Priority"
              type="number"
              value={formData.priority}
              onChange={(value) => updateFormField("priority", value)}
            />

            <Input
              label="Effective From"
              type="date"
              value={formData.effectiveFrom}
              onChange={(value) => updateFormField("effectiveFrom", value)}
            />

            <Input
              label="Effective To"
              type="date"
              value={formData.effectiveTo}
              onChange={(value) => updateFormField("effectiveTo", value)}
            />
          </Box>

          <Typography sx={{ mt: 1, fontWeight: 600 }}>Validation</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <FormControlLabel
              sx={{ gridColumn: "1 / span 2", mb: 0.5 }}
              control={
                <Checkbox
                  checked={!!formData.cutoffEnabled}
                  onChange={(_event, checked) =>
                    updateFormField("cutoffEnabled", checked)
                  }
                />
              }
              label="Enable Submit-By Cutoff"
            />

            <Input
              label="Submit By Time"
              type="time"
              value={formData.cutoffLocalTime}
              disabled={!formData.cutoffEnabled}
              onChange={(value) => updateFormField("cutoffLocalTime", value)}
            />

            <Select
              label="Cutoff Timezone"
              value={formData.cutoffTimezoneSource}
              items={cutoffTimezoneItems}
              disabled={!formData.cutoffEnabled}
              onChange={(value) =>
                updateFormField("cutoffTimezoneSource", value)
              }
            />

            <Select
              label="Missed Cutoff Behavior"
              value={formData.missedCutoffPromptMode}
              items={missedCutoffModeItems}
              disabled={!formData.cutoffEnabled}
              onChange={(value) =>
                updateFormField("missedCutoffPromptMode", value)
              }
            />

            <Input
              label="Min Dollar"
              type="number"
              value={formData.minDollar}
              onChange={(value) => updateFormField("minDollar", value)}
            />
            <Input
              label="Max Dollar"
              type="number"
              value={formData.maxDollar}
              onChange={(value) => updateFormField("maxDollar", value)}
            />
            <Input
              label="Min Quantity"
              type="number"
              value={formData.minQuantity}
              onChange={(value) => updateFormField("minQuantity", value)}
            />
            <Input
              label="Max Quantity"
              type="number"
              value={formData.maxQuantity}
              onChange={(value) => updateFormField("maxQuantity", value)}
            />
            <Select
              label="Dollar Severity"
              value={formData.dollarSeverity}
              items={severityItems}
              onChange={(value) => updateFormField("dollarSeverity", value)}
            />
            <Select
              label="Quantity Severity"
              value={formData.quantitySeverity}
              items={severityItems}
              onChange={(value) => updateFormField("quantitySeverity", value)}
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={!!formData.isActive}
                onChange={(_event, checked) =>
                  updateFormField("isActive", checked)
                }
              />
            }
            label="Active"
          />
        </Box>
      </Dialog_Flex>

      <Dialog_YesNo
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        title="Delete Policy"
        message="Delete this policy rule? This cannot be undone."
        onYes={() => deletePolicy()}
        onNo={() => setOpenDeleteConfirm(false)}
      />
    </>
  );
};

export default OrdersPolicyPage;
